/**
 * Video upload helpers.
 *
 * Strategy (post-incident):
 *   1. Never trust the browser's File.type — iPhones, Android cameras and
 *      MediaRecorder all lie or omit. The only reliable signal is the
 *      actual byte container, so we sniff the first 32 bytes.
 *   2. Map the sniffed container to a *browser-decodable* extension and
 *      Content-Type. QuickTime MOV with an H.264 stream is byte-compatible
 *      with MP4 and is served as `video/mp4`. Real WebM stays as WebM.
 *   3. If the container can't be identified we still default to MP4 since
 *      that is the most universally playable HTML5 video format.
 *
 * What we explicitly do NOT do:
 *   - Transcode in the browser (no ffmpeg.wasm yet — too heavy).
 *   - Rely on file extension or File.type alone, which produced the previous
 *     wave of black-player failures.
 */

export type VideoContainer = 'mp4' | 'mov' | 'webm' | 'unknown';

const SNIFF_BYTES = 32;

const asciiAt = (bytes: Uint8Array, offset: number, length: number): string => {
  let out = '';
  for (let i = 0; i < length && offset + i < bytes.length; i += 1) {
    out += String.fromCharCode(bytes[offset + i]);
  }
  return out;
};

/** Inspect the first ~32 bytes of a Blob and identify the real container. */
export async function sniffVideoContainer(file: Blob): Promise<VideoContainer> {
  try {
    const slice = file.slice(0, SNIFF_BYTES);
    const buf = new Uint8Array(await slice.arrayBuffer());
    if (buf.length < 8) return 'unknown';

    // Matroska / WebM — EBML header 1A 45 DF A3
    if (buf[0] === 0x1a && buf[1] === 0x45 && buf[2] === 0xdf && buf[3] === 0xa3) {
      return 'webm';
    }

    // ISO base media file format — bytes 4..7 are 'ftyp', brand follows at 8..11.
    if (asciiAt(buf, 4, 4) === 'ftyp') {
      const brand = asciiAt(buf, 8, 4).toLowerCase();
      // QuickTime: 'qt  '. Most iPhone MOVs use this brand but the AVC1
      // bitstream is identical to MP4, so we serve them as MP4.
      if (brand.startsWith('qt')) return 'mov';
      // MP4 / fragmented MP4 / iso2 / mp42 / isom / m4v / dash …
      return 'mp4';
    }

    return 'unknown';
  } catch (err) {
    console.warn('[VideoPlaybackDebug] sniff failed', { error: String(err) });
    return 'unknown';
  }
}

/**
 * Resolve the storage extension + Content-Type for a video upload. Sniffs
 * the actual bytes first; only falls back to the File.type label when sniff
 * is inconclusive.
 */
export async function resolveVideoUploadSniffed(file: Blob): Promise<{
  ext: string;
  contentType: string;
  container: VideoContainer;
  declaredType: string;
}> {
  const declaredType = (((file as any)?.type as string) || '').toLowerCase();
  const container = await sniffVideoContainer(file);

  if (container === 'webm') {
    return { ext: 'webm', contentType: 'video/webm', container, declaredType };
  }
  if (container === 'mp4' || container === 'mov') {
    // Serve MOV as MP4 so non-Safari browsers will decode the H.264 stream.
    return { ext: 'mp4', contentType: 'video/mp4', container, declaredType };
  }

  // Unknown container — fall back to declared MIME, default to mp4.
  if (declaredType.includes('webm')) {
    return { ext: 'webm', contentType: 'video/webm', container, declaredType };
  }
  return { ext: 'mp4', contentType: 'video/mp4', container, declaredType };
}

/** Synchronous variant kept for callers that haven't been wired to await. */
export function resolveVideoUpload(file: Blob): { ext: string; contentType: string } {
  const raw = (file as any)?.type as string | undefined;
  const type = (raw || '').toLowerCase();
  if (type.includes('webm')) return { ext: 'webm', contentType: 'video/webm' };
  if (type.includes('quicktime') || type.includes('mov')) {
    return { ext: 'mp4', contentType: 'video/mp4' };
  }
  return { ext: 'mp4', contentType: 'video/mp4' };
}

export function resolveThumbnailUpload(blob: Blob): { ext: string; contentType: string } {
  const mime = (blob.type || 'image/webp').toLowerCase();
  if (mime.includes('png')) return { ext: 'png', contentType: 'image/png' };
  if (mime.includes('jpeg') || mime.includes('jpg')) return { ext: 'jpg', contentType: 'image/jpeg' };
  return { ext: 'webp', contentType: 'image/webp' };
}
